import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_URL, timeout: 15000 });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("pm_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem("pm_token");
      localStorage.removeItem("pm_user");
      window.location.reload();
    }
    return Promise.reject(err);
  }
);

async function retryRequest(fn, retries = 3, delay = 2000) {
  for (let i = 0; i <= retries; i++) {
    try {
      return await fn();
    } catch (err) {
      if (i === retries) throw err;
      await new Promise((r) => setTimeout(r, delay));
    }
  }
}

const cache = {
  save: (key, data) => {
    try { localStorage.setItem(`pm_cache_${key}`, JSON.stringify(data)); } catch {}
  },
  load: (key) => {
    try { return JSON.parse(localStorage.getItem(`pm_cache_${key}`)); } catch { return null; }
  },
};

export const authAPI = {
  login: (data) => retryRequest(() => api.post("/auth/login", data)),
  register: (data) => retryRequest(() => api.post("/auth/register", data)),
  resetPassword: (data) => retryRequest(() => api.post("/auth/reset-password", data)),
  changePassword: (data) => retryRequest(() => api.put("/auth/change-password", data)),
  me: () => retryRequest(() => api.get("/auth/me")),
};

export const passwordsAPI = {
  getAll: async () => {
    try {
      const res = await retryRequest(() => api.get("/passwords"));
      cache.save("passwords", res.data);
      return { data: res.data, offline: false };
    } catch {
      const cached = cache.load("passwords");
      if (cached) return { data: cached, offline: true };
      throw new Error("Nessuna connessione e nessun dato in cache");
    }
  },
  create: (data) => retryRequest(() => api.post("/passwords", data)),
  update: (id, data) => retryRequest(() => api.put(`/passwords/${id}`, data)),
  delete: (id) => retryRequest(() => api.delete(`/passwords/${id}`)),
};

export const billingAPI = {
  createOneTimeCheckout: (data) => api.post("/billing/sumup/checkout", data),
  verifyOneTimeCheckout: (checkoutId) => api.post(`/billing/sumup/checkout/${checkoutId}/verify`),
  subscription: () => api.get("/billing/subscription"),
};

export const adminAPI = {
  getUsers: () => retryRequest(() => api.get("/admin/users")),
  createUser: (data) => retryRequest(() => api.post("/admin/users", data)),
  updateUser: (id, data) => retryRequest(() => api.put(`/admin/users/${id}`, data)),
  resetUserPassword: (id, password) => retryRequest(() => api.put(`/admin/users/${id}/password`, { password })),
  updateStatus: (id, status) => retryRequest(() => api.put(`/admin/users/${id}/status`, { status })),
  deleteUser: (id) => retryRequest(() => api.delete(`/admin/users/${id}`)),
};

export default api;
